export type PermissionConfig = {
  name: string
  value: PermissionType
  scope: PermissionScope
  level: PermissionLevel
  allowed?: string[]
  isPromoterOnly?: boolean
}

export enum PermissionType {
  // org permissions
  READ_ORGANIZATION = 'READ_ORGANIZATION',
  WRITE_ORGANIZATION = 'WRITE_ORGANIZATION',
  MANAGE_STAFF = 'MANAGE_STAFF',
  MANAGE_CUSTOMERS = 'MANAGE_CUSTOMERS',
  MERGE_CUSTOMERS = 'MERGE_CUSTOMERS',
  EXPORT_CUSTOMERS = 'EXPORT_CUSTOMERS',
  UPDATE_ATTRIBUTIONS = 'UPDATE_ATTRIBUTIONS',
  MANAGE_ORG_API_KEYS = 'MANAGE_ORG_API_KEYS',
  // MANAGE_MICROSITES = 'MANAGE_MICROSITES',
  // venue permissions
  READ_VENUE = 'READ_VENUE',
  WRITE_VENUE = 'WRITE_VENUE',
  READ_EVENTS = 'READ_EVENTS',
  WRITE_EVENTS = 'WRITE_EVENTS',
  READ_RESERVATIONS = 'READ_RESERVATIONS',
  WRITE_RESERVATIONS = 'WRITE_RESERVATIONS',
  CHECK_IN_RESERVATIONS = 'CHECK_IN_RESERVATIONS',
  READ_GUESTLISTS = 'READ_GUESTLISTS',
  WRITE_GUESTLISTS = 'WRITE_GUESTLISTS',
  CHECK_IN_GUESTLISTS = 'CHECK_IN_GUESTLISTS',
  READ_LEADS = 'READ_LEADS',
  WRITE_LEADS = 'WRITE_LEADS',
  READ_PERFORMANCE_REPORTS = 'READ_PERFORMANCE_REPORTS',
  MANAGE_WEBHOOKS = 'MANAGE_WEBHOOKS',
  //promoter specific permissions right now
  // VIEW_RESERVATIONS_MAP = 'VIEW_RESERVATIONS_MAP',
  // UPDATE_RESERVATION_TABLES = 'UPDATE_RESERVATION_TABLES',
  // CREATE_PAYMENT_LINKS = 'CREATE_PAYMENT_LINKS',
}

export enum PermissionError {
  INVALID_PERMISSION = 'Sorry this permission is not valid',
  ALREADY_HAS_PERMISSION = 'User already has that permission',
  MEMBER_NOT_FOUND = 'That member cannot be found',
  MEMBER_PERMISSION_NOT_FOUND = 'User does not have that permission',
  PERMISSION_VALUE_REQUIRED = 'permissionValue is required',
  PERMISSION_REQUIRED = 'permission is required',
  VENUE_IDS_REQUIRED = 'venueIds is required',
  OPTIONS_REQUIRED = 'options is required',
  ID_REQUIRED = 'id is required',
  VENUE_ID_REQUIRED = 'venueId is required',
  NOTIFICATION_SETTING_ID_REQUIRED = 'notificationSettingId is required',
  TYPE_INVALID_TYPE = 'type contains an invalid type',
  TYPE_REQUIRED = 'type is required',
  REPORT_TYPE_REQUIRED = 'reportType is required',
  MANAGEMENT_USER_ID_REQUIRED = 'managementUserId is required',
  USER_ID_REQUIRED = 'userId is required',
  PARENT_ID_REQUIRED = 'parentId is required',
  RESOURCE_ID_REQUIRED = 'resourceId is required',
  ORGANIZATION_ID_REQUIRED = 'organizationId is required',
  ORGANIZATION_ID_OR_VENUE_ID_REQUIRED = 'organizationId or venueId is required',
  ORGANIZATION_ID_OR_MEMBER_ID_REQUIRED = 'organizationId or organizationMemberId is required',
  UNIQUE_VENUE_REQUIRED = 'unique venue is required',
  UNIQUE_TYPE_REQUIRED = 'unique type is required',
  ADMISSION_GROUPS_NOT_FOUND = 'admission groups not found',
  CUSTOMER_ID_REQUIRED = 'customer id required',
}

export enum PermissionTypeName {
  // org permissions
  READ_ORGANIZATION = 'Read Organization',
  WRITE_ORGANIZATION = 'Write Organization',
  MANAGE_STAFF = 'Manage Staff',
  MANAGE_CUSTOMERS = 'Manage Customers',
  MERGE_CUSTOMERS = 'Merge Customers',
  EXPORT_CUSTOMERS = 'Export Customers',
  UPDATE_ATTRIBUTIONS = 'Update Attributes',
  MANAGE_ORG_API_KEYS = 'Manage Organization API Keys',
  MANAGE_WEBHOOKS = 'Manage Webhooks',
  MANAGE_MICROSITES = 'Manage Microsites',
  // venue permissions
  WRITE_VENUE = 'Write Venue',
  READ_VENUE = 'Read Venue',
  READ_EVENTS = 'Read Events',
  WRITE_EVENTS = 'Write Events',
  READ_RESERVATIONS = 'Read Reservations',
  WRITE_RESERVATIONS = 'Write Reservations',
  CHECK_IN_RESERVATIONS = 'Check In Reservations',
  READ_GUESTLISTS = 'Read Guestlists',
  WRITE_GUESTLISTS = 'Write Guestlists',
  CHECK_IN_GUESTLISTS = 'Check In Guestlists',
  READ_LEADS = 'Read Leads',
  WRITE_LEADS = 'Write Leads',
  READ_PERFORMANCE_REPORTS = 'Read Performance Reports',
  //promoter specific permissions right now
  // VIEW_RESERVATIONS_MAP = 'View Reservations Map',
  // UPDATE_RESERVATION_TABLES = 'Update Reservation Tables',
  // CREATE_PAYMENT_LINKS = 'Create Payment Link',
}

export enum PermissionLevel {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum PermissionScope {
  VENUE = 'VENUE',
  ORGANIZATION = 'ORGANIZATION',
}

export enum PermissionEvent {
  CREATED = 'ORGANIZATION_PERMISSION_CREATED',
}

export const Permissions: Record<PermissionType, PermissionConfig> = {
  WRITE_ORGANIZATION: {
    name: 'Update Organization',
    value: PermissionType.WRITE_ORGANIZATION,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.USER,
    allowed: ['Change organization settings'],
  },
  MANAGE_ORG_API_KEYS: {
    name: 'Manage API Keys',
    value: PermissionType.MANAGE_ORG_API_KEYS,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.USER,
    allowed: ['Manage organization API keys'],
  },
  // MANAGE_MICROSITES: {
  //   name: 'Manage Microsites',
  //   value: PermissionType.MANAGE_MICROSITES,
  //   scope: PermissionScope.ORGANIZATION,
  //   level: PermissionLevel.USER,
  //   allowed: ['Manage organization microsites and microsite pages'],
  // },
  MANAGE_STAFF: {
    name: 'Manage Staff',
    value: PermissionType.MANAGE_STAFF,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.USER,
    allowed: [
      'Add and change staff',
      'Add and change promoters',
      'Manage staff notification settings',
      'View activity logs',
      'Export staff, promoters and booked bys to CSV',
    ],
  },
  MANAGE_CUSTOMERS: {
    name: 'Manage Customers',
    value: PermissionType.MANAGE_CUSTOMERS,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.USER,
    allowed: ['Add and change customers'],
  },
  MERGE_CUSTOMERS: {
    name: 'Merge Customers',
    value: PermissionType.MERGE_CUSTOMERS,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.USER,
    allowed: ['Merge customer profiles'],
  },
  EXPORT_CUSTOMERS: {
    name: 'Export Customers',
    value: PermissionType.EXPORT_CUSTOMERS,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.USER,
    allowed: ['Export CRM to CSV'],
  },
  //venue permissions
  MANAGE_WEBHOOKS: {
    name: 'Manage Webhooks',
    value: PermissionType.MANAGE_WEBHOOKS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Manage venue webhooks'],
  },
  READ_RESERVATIONS: {
    name: 'View Reservations',
    value: PermissionType.READ_RESERVATIONS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['View reservations'],
  },
  WRITE_RESERVATIONS: {
    name: 'Add/Update Reservations',
    value: PermissionType.WRITE_RESERVATIONS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Add and update reservations'],
  },
  CHECK_IN_RESERVATIONS: {
    name: 'Check In Reservations',
    value: PermissionType.CHECK_IN_RESERVATIONS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Check in reservations'],
  },
  UPDATE_ATTRIBUTIONS: {
    name: 'Update Attributions',
    value: PermissionType.UPDATE_ATTRIBUTIONS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Update attributions'],
  },
  READ_GUESTLISTS: {
    name: 'View Admissions',
    value: PermissionType.READ_GUESTLISTS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['View guestlists', 'View tickets'],
  },
  READ_LEADS: {
    name: 'View Leads',
    value: PermissionType.READ_LEADS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['View Leads'],
  },
  WRITE_LEADS: {
    name: 'Add/Update Leads',
    value: PermissionType.WRITE_LEADS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Add and update leads'],
  },
  WRITE_GUESTLISTS: {
    name: 'Add/Update Admissions',
    value: PermissionType.WRITE_GUESTLISTS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Add and update guestlists', 'Add and update tickets'],
  },
  CHECK_IN_GUESTLISTS: {
    name: 'Check In Admissions',
    value: PermissionType.CHECK_IN_GUESTLISTS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Check in guestlists', 'Check in tickets'],
  },
  WRITE_VENUE: {
    name: 'Update Venue',
    value: PermissionType.WRITE_VENUE,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: [
      'Change venue settings',
      'Manage venue floorplans, sections and tables',
      'Refund reservations',
      'Refund tickets',
      'View reservation and admission history',
      'Add and change venue inventory',
      'Add and change venue reports',
    ],
  },
  WRITE_EVENTS: {
    name: 'Update Events',
    value: PermissionType.WRITE_EVENTS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['Add and change venue events'],
  },
  READ_PERFORMANCE_REPORTS: {
    name: 'View Analytics',
    value: PermissionType.READ_PERFORMANCE_REPORTS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.USER,
    allowed: ['View venue stats and analytics'],
  },
  // VIEW_RESERVATIONS_MAP: {
  //   name: 'View reservations map',
  //   value: PermissionType.VIEW_RESERVATIONS_MAP,
  //   scope: PermissionScope.VENUE,
  //   level: PermissionLevel.USER,
  //   allowed: ['View venue floorplans'],
  //   isPromoterOnly: true,
  // },
  // UPDATE_RESERVATION_TABLES: {
  //   name: 'Assign Reservation Tables',
  //   value: PermissionType.UPDATE_RESERVATION_TABLES,
  //   scope: PermissionScope.VENUE,
  //   level: PermissionLevel.USER,
  //   allowed: ['Assign reservation tables'],
  //   isPromoterOnly: true,
  // },
  // CREATE_PAYMENT_LINKS: {
  //   name: 'Create Payment Link',
  //   value: PermissionType.CREATE_PAYMENT_LINKS,
  //   scope: PermissionScope.VENUE,
  //   level: PermissionLevel.USER,
  //   allowed: ['Create payment link'],
  //   isPromoterOnly: true,
  // },

  // ------------------- SYSTEM permissions ------------------- //
  READ_ORGANIZATION: {
    name: 'Read Organization',
    value: PermissionType.READ_ORGANIZATION,
    scope: PermissionScope.ORGANIZATION,
    level: PermissionLevel.SYSTEM,
  },
  READ_EVENTS: {
    name: 'Read Events',
    value: PermissionType.READ_EVENTS,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.SYSTEM,
  },
  READ_VENUE: {
    name: 'Read Venue',
    value: PermissionType.READ_VENUE,
    scope: PermissionScope.VENUE,
    level: PermissionLevel.SYSTEM,
  },
}
